import React from 'react';
import { BasicResourceConfigurationFormProps } from '../../../types';
import { useTranslation } from '../../../contexts/TranslationContext';
import { QuizConfigurationForm } from './QuizConfigurationForm';
import { TimeSequenceConfigurationForm } from './TimeSequenceConfigurationForm';
import { MeasurementConfigurationForm } from './MeasurementConfigurationForm';
import { CurrencyConfigurationForm } from './CurrencyConfigurationForm';
import { SortingCanvasConfigForm } from './SortingCanvasConfigForm';
import { SpatialCanvasConfigForm } from './SpatialCanvasConfigForm';
import { PatternMakerConfigForm } from './PatternMakerConfigForm';
import { DrawingBoardConfigForm } from './DrawingBoardConfigForm';
import { ObjectManipulatorConfigForm } from './ObjectManipulatorConfigForm';
import { ImageConfigurationForm } from './ImageConfigurationForm';
import { RichTextConfigurationForm } from './RichTextConfigurationForm';




export const NumberLineConfigurationForm: React.FC<BasicResourceConfigurationFormProps> = ({
  resource,
  config,
  onUpdate,
}) => {
  const { t } = useTranslation();
  
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('resourceConfig.minimumValue')}</label>
          <input
            type="number"
            value={config?.min === undefined ? resource.min : config.min}
            onChange={(e) => onUpdate({ min: parseInt(e.target.value) || 0 })}
            className="input-field"
            min="0"
            placeholder="0"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('resourceConfig.maximumValue')}</label>
          <input
            type="number"
            value={config?.max === undefined ? resource.max : config.max}
            onChange={(e) => onUpdate({ max: parseInt(e.target.value) || 20 })}
            className="input-field"
            min="0"
            placeholder="20"
          />
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('resourceConfig.tickEvery')}</label>
          <input
            type="number"
            value={config?.tick_every === undefined ? resource.tick_every : config.tick_every}
            onChange={(e) => onUpdate({ tick_every: parseInt(e.target.value) || 1 })}
            className="input-field"
            min="1"
            max="10"
            placeholder="1"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('resourceConfig.step')}</label>
          <input
            type="number"
            value={config?.step === undefined ? resource.step : config.step}
            onChange={(e) => onUpdate({ step: parseInt(e.target.value) || 1 })}
            className="input-field"
            min="1"
            max="10"
            placeholder="1"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('resourceConfig.theme')}</label>
        <select
          value={config?.theme === undefined ? resource.theme : config.theme}
          onChange={(e) => onUpdate({ theme: e.target.value })}
          className="input-field"
        >
          <option value="primary">{t('resourceConfig.primary')}</option>
          <option value="secondary">{t('resourceConfig.secondary')}</option>
          <option value="blue">{t('resourceConfig.blue')}</option>
          <option value="green">{t('resourceConfig.green')}</option>
          <option value="purple">{t('resourceConfig.purple')}</option>
        </select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('resourceConfig.showLabels')}</label>
          <input
            type="checkbox"
            checked={config?.showLabels === undefined ? resource.showLabels : config.showLabels}
            onChange={(e) => onUpdate({ showLabels: e.target.checked })}
            className="rounded border-gray-300"
          />
          <span className="ml-2 text-sm text-gray-600">{t('resourceConfig.showNumberLabels')}</span>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('resourceConfig.showTicks')}</label>
          <input
            type="checkbox"
            checked={config?.showTicks === undefined ? resource.showTicks : config.showTicks}
            onChange={(e) => onUpdate({ showTicks: e.target.checked })}
            className="rounded border-gray-300"
          />
          <span className="ml-2 text-sm text-gray-600">{t('resourceConfig.showTickMarks')}</span>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('resourceConfig.interactive')}</label>
        <input
          type="checkbox"
          checked={config?.interactive === undefined ? resource.interactive : config.interactive}
          onChange={(e) => onUpdate({ interactive: e.target.checked })}
          className="rounded border-gray-300"
        />
        <span className="ml-2 text-sm text-gray-600">{t('resourceConfig.allowUserInteraction')}</span>
      </div>

      <div className="bg-blue-50 rounded-lg p-3">
        <p className="text-sm text-blue-700">
          <strong>{t('resourceConfig.preview')}:</strong> {t('resourceConfig.numberLineFrom')} {config?.min || resource.min || 0} {t('resourceConfig.to')} {config?.max || resource.max || 20} {t('resourceConfig.withTicksEvery')} {config?.tick_every || resource.tick_every || 1} {t('resourceConfig.unit')}
          {(config?.interactive !== false || resource.interactive !== false) ? ` (${t('resourceConfig.interactiveDisplay')})` : ` (${t('resourceConfig.displayOnly')})`}
        </p>
      </div>
    </div>
  );
};

export const TenFrameConfigurationForm: React.FC<BasicResourceConfigurationFormProps> = ({
  resource,
  config,
  onUpdate
}) => {
  const { t } = useTranslation();
  
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('resourceConfig.rows')}</label>
        <input
          type="number"
          value={config?.rows === undefined ? resource.rows : config.rows}
          onChange={(e) => onUpdate({ rows: parseInt(e.target.value) })}
          className="input-field"
          min="1"
          max="5"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('resourceConfig.columns')}</label>
        <input
          type="number"
          value={config?.cols === undefined ? resource.cols : config.cols}
          onChange={(e) => onUpdate({ cols: parseInt(e.target.value) })}
          className="input-field"
          min="1"
          max="10"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('resourceConfig.doubleFrame')}</label>
        <input
          type="checkbox"
          checked={config?.double_frame === undefined ? resource.double_frame : config.double_frame}
          onChange={(e) => onUpdate({ double_frame: e.target.checked })}
          className="rounded border-gray-300"
        />
        <span className="ml-2 text-sm text-gray-600">{t('resourceConfig.showTwoFrames')}</span>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('resourceConfig.dragCounters')}</label>
        <input
          type="checkbox"
          checked={config?.drag_counters === undefined ? resource.drag_counters : config.drag_counters}
          onChange={(e) => onUpdate({ drag_counters: e.target.checked })}
          className="rounded border-gray-300"
        />
        <span className="ml-2 text-sm text-gray-600">{t('resourceConfig.allowDraggingCounters')}</span>
      </div>
    </div>
  );
}

export const CountObjectsCanvasConfigurationForm: React.FC<BasicResourceConfigurationFormProps> = ({
  resource,
  config,
  onUpdate
}) => {
  const { t } = useTranslation();

  // Check if using new object configuration structure
  const hasObjectConfig = resource.objects === undefined || (Array.isArray(resource.objects) && resource.objects.length > 0);
  const currentObjects = config?.objects || resource.objects || [];

  const addObjectGroup = () => {
    const newObjects = [...currentObjects, { type: 'circle', count: 5, color: 'blue' }];
    onUpdate({ objects: newObjects });
  };

  const removeObjectGroup = (index: number) => {
    const newObjects = currentObjects.filter((_: any, i: number) => i !== index);
    onUpdate({ objects: newObjects });
  };

  const updateObjectGroup = (index: number, field: string, value: any) => {
    const newObjects = [...currentObjects];
    newObjects[index] = { ...newObjects[index], [field]: value };
    onUpdate({ objects: newObjects });
  };

  return (
    <div className="space-y-4">
      {/* Legacy single object count (for backward compatibility) */}
      {!hasObjectConfig && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('resourceConfig.maxObjects')}</label>
          <input
            type="number"
            value={config?.objects === undefined ? resource.objects : config.objects}
            onChange={(e) => onUpdate({ objects: parseInt(e.target.value) })}
            className="input-field"
            min="1"
          />
        </div>
      )}

      {/* New multiple object groups configuration */}
      {hasObjectConfig && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('resourceConfig.objectGroups')}</label>
          <div className="space-y-3">
            {currentObjects.map((obj: any, index: number) => (
              <div key={index} className="p-3 border border-gray-200 rounded-lg bg-gray-50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">{t('resourceConfig.objectGroup')} {index + 1}</span>
                  <button
                    type="button"
                    onClick={() => removeObjectGroup(index)}
                    className="text-red-500 hover:text-red-700 text-sm"
                  >
                    {t('resourceConfig.remove')}
                  </button>
                </div>
                
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{t('resourceConfig.objectType')}</label>
                    <select
                      value={obj.type || 'circle'}
                      onChange={(e) => updateObjectGroup(index, 'type', e.target.value)}
                      className="input-field text-sm"
                    >
                      <option value="circle">{t('resourceConfig.circle')}</option>
                      <option value="square">{t('resourceConfig.square')}</option>
                      <option value="triangle">{t('resourceConfig.triangle')}</option>
                      <option value="star">{t('resourceConfig.star')}</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{t('resourceConfig.objectCount')}</label>
                    <input
                      type="number"
                      value={obj.count || 5}
                      onChange={(e) => updateObjectGroup(index, 'count', parseInt(e.target.value))}
                      className="input-field text-sm"
                      min="1"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{t('resourceConfig.objectColor')}</label>
                    <select
                      value={obj.color || 'blue'}
                      onChange={(e) => updateObjectGroup(index, 'color', e.target.value)}
                      className="input-field text-sm"
                    >
                      <option value="blue">{t('resourceConfig.blue')}</option>
                      <option value="red">{t('resourceConfig.red')}</option>
                      <option value="green">{t('resourceConfig.green')}</option>
                      <option value="yellow">{t('resourceConfig.yellow')}</option>
                      <option value="purple">{t('resourceConfig.purple')}</option>
                      <option value="orange">{t('resourceConfig.orange')}</option>
                    </select>
                  </div>
                </div>
              </div>
            ))}
            
            <button
              type="button"
              onClick={addObjectGroup}
              className="w-full p-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-gray-400 hover:text-gray-600 transition-colors"
            >
              {t('resourceConfig.addObjectGroup')}
            </button>
          </div>
        </div>
      )}

      {/* Question field */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('resourceConfig.question')}</label>
        <input
          type="text"
          value={config?.question === undefined ? resource.question : config.question}
          onChange={(e) => onUpdate({ question: e.target.value })}
          className="input-field"
          placeholder={t('resourceConfig.questionPlaceholder')}
        />
      </div>

      {/* Display options */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('resourceConfig.showCount')}</label>
        <input
          type="checkbox"
          checked={config?.showCount === undefined ? resource.showCount : config.showCount}
          onChange={(e) => onUpdate({ showCount: e.target.checked })}
          className="rounded border-gray-300"
        />
        <span className="ml-2 text-sm text-gray-600">{t('resourceConfig.showObjectCount')}</span>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('resourceConfig.allowInteraction')}</label>
        <input
          type="checkbox"
          checked={config?.allowInteraction === undefined ? resource.allowInteraction : config.allowInteraction}
          onChange={(e) => onUpdate({ allowInteraction: e.target.checked })}
          className="rounded border-gray-300"
        />
        <span className="ml-2 text-sm text-gray-600">{t('resourceConfig.allowObjectInteraction')}</span>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('resourceConfig.randomizeObjects')}</label>
        <input
          type="checkbox"
          checked={config?.randomize === undefined ? resource.randomize : config.randomize}
          onChange={(e) => onUpdate({ randomize: e.target.checked })}
          className="rounded border-gray-300"
        />
        <span className="ml-2 text-sm text-gray-600">{t('resourceConfig.randomizeObjectPositions')}</span>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('resourceConfig.touchFeedback')}</label>
        <select
          value={config?.touch_feedback === undefined ? resource.touch_feedback : config.touch_feedback}
          onChange={(e) => onUpdate({ touch_feedback: e.target.value })}
          className="input-field"
        >
          <option value="ping">{t('resourceConfig.ping')}</option>
          <option value="pulse">{t('resourceConfig.pulse')}</option>
          <option value="bounce">{t('resourceConfig.bounce')}</option>
          <option value="shake">{t('resourceConfig.shake')}</option>
          <option value="none">{t('resourceConfig.none')}</option>
        </select>
      </div>
    </div>
  );
}

export const TimerConfigurationForm: React.FC<BasicResourceConfigurationFormProps> = ({
  resource,
  config,
  onUpdate
}) => {
  const { t } = useTranslation();
  
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('resourceConfig.durationSeconds')}</label>
        <input
          type="number"
          value={config?.seconds === undefined ? resource.seconds : config.seconds}
          onChange={(e) => onUpdate({ seconds: parseInt(e.target.value) })}
          className="input-field"
          min="1"
          max="3600"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('resourceConfig.autoLaunch')}</label>
        <input
          type="checkbox"
          checked={config?.auto_launch === undefined ? resource.auto_launch : config.auto_launch}
          onChange={(e) => onUpdate({ auto_launch: e.target.checked })}
          className="rounded border-gray-300"
        />
        <span className="ml-2 text-sm text-gray-600">{t('resourceConfig.autoLaunch')}</span>
      </div>
    </div>
  );
}

export const FlashcardSetConfigurationForm: React.FC<BasicResourceConfigurationFormProps> = ({
  resource,
  config,
  onUpdate
}) => {
  const { t } = useTranslation();

  const mergedResource = { ...resource, ...config };

  console.log('mergedResource', mergedResource);

  // Support both legacy structure and new config structure
  const getConfigValue = (key: string, defaultValue: any) => {
    if (mergedResource && mergedResource[key] !== undefined) {
      return mergedResource[key];
    }
    return defaultValue;
  };

  const cards = mergedResource?.cards || mergedResource?.items || [];
  const autoFlip = getConfigValue('autoFlip', false);
  const showProgress = getConfigValue('showProgress', true);
  const autoAdvanceSeconds = getConfigValue('auto_advance_seconds', 3);


  const addCard = () => {
    const newCards = [...cards, { front: '', back: '', answer: '' }];
    onUpdate({ cards: newCards });
  };

  const removeCard = (index: number) => {
    const newCards = cards.filter((_: any, i: number) => i !== index);
    onUpdate({ cards: newCards });
  };

  const updateCard = (index: number, field: string, value: string) => {
    const newCards = [...cards];
    newCards[index] = { ...newCards[index], [field]: value };
    onUpdate({ cards: newCards });
  };
  
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">{t('resourceConfig.flashcards')}</label>
        <div className="space-y-3">
          {cards.map((card: any, index: number) => (
            <div key={index} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">{t('resourceConfig.card')} {index + 1}</span>
                <button
                  type="button"
                  onClick={() => removeCard(index)}
                  className="text-red-600 hover:text-red-800 text-sm"
                >
                  {t('resourceConfig.remove')}
                </button>
              </div>
              <div className="grid grid-cols-1 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('resourceConfig.front')}</label>
                  <input
                    type="text"
                    value={card.front || ''}
                    onChange={(e) => updateCard(index, 'front', e.target.value)}
                    className="input-field text-sm"
                    placeholder="15"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('resourceConfig.back')}</label>
                  <input
                    type="text"
                    value={card.back || ''}
                    onChange={(e) => updateCard(index, 'back', e.target.value)}
                    className="input-field text-sm"
                    placeholder="Comparer : 15 ___ 12"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">{t('resourceConfig.answer')}</label>
                  <input
                    type="text"
                    value={card.answer || ''}
                    onChange={(e) => updateCard(index, 'answer', e.target.value)}
                    className="input-field text-sm"
                    placeholder="15 > 12"
                  />
                </div>
              </div>
            </div>
          ))}
          
          <button
            type="button"
            onClick={addCard}
            className="w-full p-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-gray-400 hover:text-gray-600 transition-colors"
          >
            {t('resourceConfig.addCard')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('resourceConfig.autoFlip')}</label>
          <input
            type="checkbox"
            checked={autoFlip}
            onChange={(e) => onUpdate({ autoFlip: e.target.checked })}
            className="rounded border-gray-300"
          />
          <span className="ml-2 text-sm text-gray-600">{t('resourceConfig.autoFlipCards')}</span>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('resourceConfig.showProgress')}</label>
          <input
            type="checkbox"
            checked={showProgress}
            onChange={(e) => onUpdate({ showProgress: e.target.checked })}
            className="rounded border-gray-300"
          />
          <span className="ml-2 text-sm text-gray-600">{t('resourceConfig.showCardProgress')}</span>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('resourceConfig.autoAdvanceSeconds')}</label>
        <input
          type="number"
          value={autoAdvanceSeconds}
          onChange={(e) => onUpdate({ auto_advance_seconds: parseInt(e.target.value) || 3 })}
          className="input-field"
          min="1"
          max="60"
          placeholder="3"
        />
        <span className="text-sm text-gray-600">{t('resourceConfig.autoAdvanceSecondsDescription')}</span>
      </div>
    </div>
  );
};

export const GridActivityConfigurationForm: React.FC<BasicResourceConfigurationFormProps> = ({
  resource,
  config,
  onUpdate
}) => {
  const { t } = useTranslation();
  
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('resourceConfig.rows')}</label>
        <input
          type="number"
          value={config?.rows === undefined ? resource.rows : config.rows}
          onChange={(e) => onUpdate({ rows: parseInt(e.target.value) })}
          className="input-field"
          min="1"
          max="10"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('resourceConfig.columns')}</label>
        <input
          type="number"
          value={config?.cols === undefined ? resource.cols : config.cols}
          onChange={(e) => onUpdate({ cols: parseInt(e.target.value) })}
          className="input-field"
          min="1"
          max="10"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('resourceConfig.bankCommaSeparated')}</label>
        <input
          type="text"
          value={(config?.bank || []).join(', ')}
          onChange={(e) => {
            const bank = e.target.value.split(',').map(item => parseInt(item.trim())).filter(n => !isNaN(n));
            onUpdate({ bank: bank });
          }}
          className="input-field"
          placeholder="0, 1, 2, 3, 4, 5"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('resourceConfig.mode')}</label>
        <select
          value={config?.mode === undefined ? resource.mode : config.mode}
          onChange={(e) => onUpdate({ mode: e.target.value })}
          className="input-field"
        >
          <option value="read_and_select">{t('resourceConfig.readAndSelect')}</option>
          <option value="match_pairs">{t('resourceConfig.matchPairs')}</option>
          <option value="fill_blanks">{t('resourceConfig.fillBlanks')}</option>
        </select>
      </div>
    </div>
  );
}

export const AudioBankConfigurationForm: React.FC<BasicResourceConfigurationFormProps> = ({
  resource,
  config,
  onUpdate
}) => {
  const { t } = useTranslation();
  
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('resourceConfig.wordsCommaSeparated')}</label>
        <input
          type="text"
          value={(config?.words || []).join(', ')}
          onChange={(e) => {
            const words = e.target.value.split(',').map(word => word.trim()).filter(word => word.length > 0);
            onUpdate({ words: words });
          }}
          className="input-field"
          placeholder="zero, one, two, three"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('resourceConfig.autoPlay')}</label>
        <input
          type="checkbox"
          checked={config?.auto_play === undefined ? resource.auto_play : config.auto_play}
          onChange={(e) => onUpdate({ auto_play: e.target.checked })}
          className="rounded border-gray-300"
        />
        <span className="ml-2 text-sm text-gray-600">{t('resourceConfig.playAudioAutomatically')}</span>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('resourceConfig.loop')}</label>
        <input
          type="checkbox"
          checked={config?.loop === undefined ? resource.loop : config.loop}
          onChange={(e) => onUpdate({ loop: e.target.checked })}
          className="rounded border-gray-300"
        />
        <span className="ml-2 text-sm text-gray-600">{t('resourceConfig.loopAudio')}</span>
      </div>
    </div>
  );
}

export const MatchingObjectsConfigurationForm: React.FC<BasicResourceConfigurationFormProps> = ({
  resource,
  config,
  onUpdate
}) => {
  const { t } = useTranslation();
  
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('resourceConfig.maximumValue')}</label>
        <input
          type="number"
          value={config?.max_value === undefined ? resource.max_value : config.max_value}
          onChange={(e) => onUpdate({ max_value: parseInt(e.target.value) || 10 })}
          className="input-field"
          min="1"
          max="20"
          placeholder="10"
        />
        <p className="text-xs text-gray-500 mt-1">{t('resourceConfig.highestNumberToInclude')}</p>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('resourceConfig.numberOfItems')}</label>
        <input
          type="number"
          value={config?.item_count === undefined ? resource.item_count : config.item_count}
          onChange={(e) => onUpdate({ item_count: parseInt(e.target.value) || 4 })}
          className="input-field"
          min="2"
          max="8"
          placeholder="4"
        />
        <p className="text-xs text-gray-500 mt-1">{t('resourceConfig.howManyUniqueNumbers')}</p>
      </div>

      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700">{t('resourceConfig.representationTypes')}</label>
        
        <div className="space-y-2">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={config?.include_objects === undefined ? resource.include_objects : config.include_objects}
              onChange={(e) => onUpdate({ include_objects: e.target.checked })}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-600">{t('resourceConfig.includeObjects')}</span>
          </label>
          
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={config?.include_numerals === undefined ? resource.include_numerals : config.include_numerals}
              onChange={(e) => onUpdate({ include_numerals: e.target.checked })}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-600">{t('resourceConfig.includeNumerals')}</span>
          </label>
          
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={config?.include_ten_frames === undefined ? resource.include_ten_frames : config.include_ten_frames}
              onChange={(e) => onUpdate({ include_ten_frames: e.target.checked })}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-600">{t('resourceConfig.includeTenFrames')}</span>
          </label>
          
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={config?.include_words === undefined ? resource.include_words : config.include_words}
              onChange={(e) => onUpdate({ include_words: e.target.checked })}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-600">{t('resourceConfig.includeNumberWords')}</span>
          </label>
        </div>
        
        <p className="text-xs text-gray-500">{t('resourceConfig.selectAtLeastTwo')}</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('resourceConfig.objectLayout')}</label>
        <select
          value={config?.object_layout === undefined ? resource.object_layout : config.object_layout}
          onChange={(e) => onUpdate({ object_layout: e.target.value })}
          className="input-field"
        >
          <option value="random_clustered">{t('resourceConfig.randomClustered')}</option>
          <option value="linear">{t('resourceConfig.linear')}</option>
          <option value="two_groups">{t('resourceConfig.twoGroups')}</option>
          <option value="circular">{t('resourceConfig.circular')}</option>
        </select>
        <p className="text-xs text-gray-500 mt-1">{t('resourceConfig.howObjectsAreArranged')}</p>
      </div>

      <div className="bg-blue-50 rounded-lg p-3">
        <p className="text-sm text-blue-700">
          <strong>{t('resourceConfig.preview')}:</strong> {t('resourceConfig.matchingActivityWith')} {config?.item_count === undefined ? resource.item_count : config.item_count} {t('resourceConfig.uniqueNumbersUpTo')} {config?.max_value === undefined ? resource.max_value : config.max_value}.
          <br />
          {t('resourceConfig.includes')}: {[
            config?.include_objects === undefined ? resource.include_objects : config.include_objects && t('matchingObjects.objects'),
            config?.include_numerals === undefined ? resource.include_numerals : config.include_numerals && t('matchingObjects.numerals'), 
            config?.include_ten_frames === undefined ? resource.include_ten_frames : config.include_ten_frames && t('matchingObjects.tenFrames'),
            config?.include_words === undefined ? resource.include_words : config.include_words && t('matchingObjects.words')
          ].filter(Boolean).join(', ') || t('resourceConfig.noRepresentationsSelected')}
        </p>
      </div>
    </div>
  );
}

export const DefaultConfigurationForm: React.FC<BasicResourceConfigurationFormProps> = ({
  resource,
  config
}) => {
  const { t } = useTranslation();
  
  return (
    <div className="text-center py-8 text-gray-500">
      <p>{resource.type}</p>
      <p>{config?.resourceId === undefined ? resource.id : config.resourceId}</p>
      <p>{t('resourceConfig.noConfigurationOptions')}</p>
    </div>
  );
}

export const BasicResourceConfigurationForm: React.FC<BasicResourceConfigurationFormProps> = ({
  resource,
  config,
  onUpdate,
  idEditable = false
}) => {
  const { t } = useTranslation();
  
  const renderConfigurationFields = () => {
    switch (resource.type) {
      case 'number_line':
        return (
          <NumberLineConfigurationForm 
            resource={resource} 
            config={config}
            onUpdate={onUpdate} 
          />
        );

      case 'ten_frame':
        return (
          <TenFrameConfigurationForm 
            resource={resource}
            config={config}
            onUpdate={onUpdate}
          />
        );

      case 'count_objects_canvas':
        return (
          <CountObjectsCanvasConfigurationForm
            resource={resource}
            config={config}
            onUpdate={onUpdate}
          />
        );

      case 'timer':
        return (
          <TimerConfigurationForm
            resource={resource}
            config={config}
            onUpdate={onUpdate}
          />
        );

      case 'flashcard_set':
        return (
          <FlashcardSetConfigurationForm
            resource={resource}
            config={config}
            onUpdate={onUpdate}
          />
        );

      case 'time_sequence':
        return (
          <TimeSequenceConfigurationForm
            resource={resource}
            config={config}
            onUpdate={onUpdate}
          />
        );

      case 'measurement':
        return (
          <MeasurementConfigurationForm
            resource={resource}
            config={config}
            onUpdate={onUpdate}
          />
        );

      case 'currency':
        return (
          <CurrencyConfigurationForm
            resource={resource}
            config={config}
            onUpdate={onUpdate}
          />
        );

      case 'grid_activity':
        return (
          <GridActivityConfigurationForm
            resource={resource}
            config={config}
            onUpdate={onUpdate}
          />
        );

      case 'audio_bank':
        return (
          <AudioBankConfigurationForm
            resource={resource}
            config={config}
            onUpdate={onUpdate}
          />
        );

      case 'matching_objects':
        return (
          <MatchingObjectsConfigurationForm
            resource={resource}
            config={config}
            onUpdate={onUpdate}
          />
        );

      case 'quiz':
        return (
          <QuizConfigurationForm
            resource={resource}
            config={config}
            onUpdate={onUpdate}
          />
        );

      case 'sorting_canvas':
        return (
          <SortingCanvasConfigForm
            resource={resource}
            config={config}
            onConfigChange={onUpdate}
          />
        );
      case 'spatial_canvas':
        return (
          <SpatialCanvasConfigForm
            resource={resource}
            config={config}
            onConfigChange={onUpdate}
          />
        );
      case 'pattern_maker':
        return (
          <PatternMakerConfigForm
            resource={resource}
            config={config}
            onConfigChange={onUpdate}
          />
        );
      case 'drawing_board':
        return (
          <DrawingBoardConfigForm
            resource={resource}
            config={config}
            onConfigChange={onUpdate}
          />
        );  
      case 'object_manipulator':
        return (
          <ObjectManipulatorConfigForm
            resource={resource}
            config={config}
            onConfigChange={onUpdate}
          />
        );

      case 'image':
        return (
          <ImageConfigurationForm
            resource={resource}
            config={config}
            onUpdate={onUpdate}
          />
        );

      case 'rich_text':
        return (
          <RichTextConfigurationForm
            resource={resource}
            config={config}
            onUpdate={onUpdate}
          />
        );

      default:
        return (
          <DefaultConfigurationForm
            resource={resource}
            config={config}
            onUpdate={onUpdate}
          />
        );
    }
  };

  return (
    <div className="space-y-4">
      {idEditable && <div className="space-y-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">{t('resourceConfig.id')}</label>
        <input
          type="text"
          value={config?.resourceId === undefined ? resource.id : config.resourceId}
          onChange={(e) => onUpdate({ resourceId: e.target.value })}
          className="input-field"
        />
      </div>}
      {renderConfigurationFields()}
    </div>
  );
};
